import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { Metric, MetricMeasure } from '@/types/metric';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const metricRepository = {
  async getMetrics({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<Metric[]>> {
    try {
      const path = `/api/dugout/person_metrics/${personId}/metrics/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get devices.');
    }
  },
  async getMeasure({
    host,
    key,
    personId
  }: {
    host: string;
    key: string;
    personId: string;
  }): Promise<RepositoryResponse<MetricMeasure>> {
    try {
      const path = `/api/dugout/person_metrics/${personId}/measures/${key}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get devices.');
    }
  }
};

const MetricRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async metrics(personId: string): Promise<RepositoryResponse<Metric[]>> {
      return metricRepository.getMetrics({
        host: host,
        personId
      });
    },
    async measure(personId: string, key: string): Promise<RepositoryResponse<MetricMeasure>> {
      return metricRepository.getMeasure({ host, personId, key });
    }
  };
};

export default MetricRepository;
